import { App, Button, Modal, Tag } from "antd";
import { useMemberSession } from "@/entities/member";
import {
  useConfirmNotificationPopup,
  useNotificationPopup,
  useTrackNotificationClick,
  resolveNotificationActionUrl,
  shouldDisplayNotificationPopup,
} from "@/entities/notification";

function formatPopupDescription(value: string) {
  return value.replace(/\\n/g, "\n");
}

export function MemberLoginPopupQueue() {
  const { message } = App.useApp();
  const authenticated = useMemberSession((state) => state.authenticated);
  const promptVersion = useMemberSession(
    (state) => state.loginNotificationPromptVersion,
  );
  const { data: notification } = useNotificationPopup(promptVersion);
  const confirmNotification = useConfirmNotificationPopup();
  const trackNotificationClick = useTrackNotificationClick();

  const activeNotification =
    notification && shouldDisplayNotificationPopup(notification)
      ? notification
      : undefined;

  const closeCurrent = async () => {
    try {
      if (activeNotification) {
        await confirmNotification.mutateAsync(activeNotification.id);
      }
    } catch (error) {
      void message.error(
        error instanceof Error ? error.message : "弹窗确认失败",
      );
    }
  };

  const openNotificationAction = async () => {
    if (!activeNotification?.actionUrl) return;
    try {
      const response = await trackNotificationClick.mutateAsync(
        activeNotification.id,
      );
      const target = resolveNotificationActionUrl(
        response.data?.actionUrl ?? activeNotification.actionUrl,
        window.location.origin,
      );
      if (!target) {
        void message.error("消息跳转地址无效");
        return;
      }
      await confirmNotification.mutateAsync(activeNotification.id);
      if (target.type === "external") {
        window.open(target.url, "_blank", "noopener,noreferrer");
      } else {
        window.location.assign(target.url);
      }
    } catch (error) {
      void message.error(
        error instanceof Error ? error.message : "消息跳转失败",
      );
    }
  };

  const busy =
    confirmNotification.isPending || trackNotificationClick.isPending;

  return (
    <Modal
      open={Boolean(authenticated && activeNotification)}
      centered
      width={460}
      footer={
        <>
          <Button loading={busy} onClick={() => void closeCurrent()}>
            知道了
          </Button>
          {activeNotification?.actionText && activeNotification.actionUrl && (
            <Button
              type="primary"
              loading={busy}
              onClick={() => void openNotificationAction()}
            >
              {activeNotification.actionText}
            </Button>
          )}
        </>
      }
      onCancel={() => void closeCurrent()}
      closable={!busy}
      maskClosable={!busy}
      destroyOnHidden
      className="member-login-popup-modal"
      title={activeNotification?.title}
    >
      {activeNotification && (
        <div className="member-login-popup">
          <div className="member-login-popup-tags">
            {activeNotification.newMessage && (
              <Tag color="blue" bordered={false}>
                NEW
              </Tag>
            )}
            {activeNotification.priority !== "NORMAL" && (
              <Tag
                color={
                  activeNotification.priority === "URGENT" ? "red" : "gold"
                }
                bordered={false}
              >
                {activeNotification.priority === "URGENT"
                  ? "紧急消息"
                  : "重要消息"}
              </Tag>
            )}
          </div>
          {activeNotification.coverUrl && (
            <img
              src={activeNotification.coverUrl}
              alt=""
              style={{
                width: "100%",
                maxHeight: 220,
                objectFit: "cover",
                borderRadius: 10,
                marginBottom: 12,
              }}
            />
          )}
          <strong>{activeNotification.summary}</strong>
          <p>{formatPopupDescription(activeNotification.body)}</p>
        </div>
      )}
    </Modal>
  );
}
